<template>
  <guest-layout background-class="bg-white" :color-scheme="'light'" :poper-absolute="true">

    <main class="mt-8 sm:mt-0">

      <div class="relative isolate overflow-hidden bg-gradient-to-b from-primary-100/20">
        <div class="mx-auto max-w-7xl pb-24 pt-10 sm:pb-32 lg:grid lg:grid-cols-2 lg:gap-x-8 lg:px-8 lg:py-40">
          <div v-motion-slide-left class="px-6 lg:px-0 lg:pt-4">
            <div class="mx-auto max-w-2xl">
              <div class="max-w-lg">
                <h3 class="mt-10 text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl">{{ __('REST API: Integrate LIVCK into your') }}</h3>
                {{ ' ' }}
                <span class="mt-10 text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl text-primary-500">
                  <vue-writer
                      :array="[__('Platform.'), __('Website.'), __('App.'), __('Workflow.')]"/>
                </span>
                <p class="mt-6 text-lg leading-8 text-gray-600">{{ __('In order to improve the user experience and to maintain transparency with your customers, you should integrate LIVCK into your own applications, in particular into your web site. Our API can be used to provide real-time service status and updates directly on your platform.') }}</p>
                <div class="mt-10 flex items-center gap-x-6">
                  <a target="_blank" href="https://help.livck.com" class="rounded-md bg-primary-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600">{{ __('Documentation') }}</a>
                  <a target="_blank" href="https://github.com/LIVCK" class="text-sm font-semibold leading-6 text-gray-900">{{ __('View on GitHub') }} <span aria-hidden="true">→</span></a>
                </div>
              </div>
            </div>
          </div>
          <div class="mt-32 sm:mt-40 md:mx-auto md:max-w-2xl lg:mx-0 lg:w-screen">
            <div class="absolute inset-y-0 right-1/2 -z-10 -mr-10 w-[200%] skew-x-[-30deg] bg-white shadow-xl shadow-primary-600/10 ring-1 ring-primary-50 md:-mr-20 lg:-mr-36" aria-hidden="true" />
            <div v-motion-up-down class="shadow-lg md:rounded-3xl">
              <div class="bg-primary-500 [clip-path:inset(0)] md:[clip-path:inset(0_round_theme(borderRadius.3xl))]">
                <div class="absolute -inset-y-px left-1/2 -z-10 ml-10 w-[200%] skew-x-[-30deg] bg-primary-100 opacity-20 ring-1 ring-inset ring-white md:ml-20 lg:ml-36" aria-hidden="true" />
                <div class="relative px-6 pt-8 sm:pt-16 md:pl-16 md:pr-0">
                  <div class="not-prose my-6 overflow-hidden rounded-2xl bg-zinc-900 shadow-md dark:ring-1 dark:ring-white/10">
                    <div class="flex min-h-[calc(theme(spacing.12)+1px)] flex-wrap items-start gap-x-4 border-b border-zinc-700 bg-zinc-800 px-4 dark:border-zinc-800 dark:bg-transparent">
                      <div class="-mb-px flex gap-4 text-xs font-medium" role="tablist" aria-orientation="horizontal">
                        <button
                            class="border-b py-4 transition focus:[&amp;:not(:focus-visible)]:outline-none border-emerald-500 text-emerald-400"
                            id="headlessui-tabs-tab-:r4l:" role="tab" type="button" aria-selected="true" tabindex="0"
                            data-headlessui-state="selected" aria-controls="headlessui-tabs-panel-:r4p:">Example cURL
                        </button>
                      </div>
                    </div>
                    <div>
                      <div id="headlessui-tabs-panel-:r4p:" role="tabpanel" tabindex="0"
                           data-headlessui-state="selected" aria-labelledby="headlessui-tabs-tab-:r4l:">
                        <div class="group dark:bg-white/2.5">
                          <div
                              class="flex h-9 items-center gap-2 border-y border-b-white/7.5 border-t-transparent bg-white/2.5 bg-zinc-900 px-4 dark:border-b-white/5 dark:bg-white/1">
                            <div class="dark flex"><span
                                class="font-mono text-[0.625rem] font-semibold leading-6 text-emerald-500 dark:text-emerald-400">GET</span>
                            </div>
                            <span class="h-0.5 w-0.5 rounded-full bg-zinc-500"></span><span
                              class="font-mono text-xs text-zinc-400">/api/v3/categories</span></div>
                          <div class="relative"><pre class="overflow-x-auto p-4 text-xs text-white"><code
                              class="language-bash"><span><span style="color: var(--shiki-color-text)">curl -G https://status.livck.com/api/v3/categories</span></span></code></pre>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="pointer-events-none absolute inset-0 ring-1 ring-inset ring-black/10 md:rounded-3xl" aria-hidden="true" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="absolute inset-x-0 bottom-0 -z-10 h-24 bg-gradient-to-t from-white sm:h-32" />
      </div>

      <div class="bg-white py-24 sm:py-32">
        <div class="mx-auto max-w-7xl px-6 lg:px-8">
          <div class="mx-auto max-w-2xl lg:text-center">
            <h2 class="text-base font-semibold leading-7 text-primary-600">{{ __('Developer-Friendly API') }}</h2>
            <p class="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">{{ __('Simplifying Integration and Monitoring') }}</p>
<!--            <p class="mt-6 text-lg leading-8 text-gray-600">Quis tellus eget adipiscing convallis sit sit eget aliquet quis. Suspendisse eget egestas a elementum pulvinar et feugiat blandit at. In mi viverra elit nunc.</p>-->
          </div>
          <div class="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-4xl">
            <dl class="grid max-w-xl grid-cols-1 gap-x-8 gap-y-10 lg:max-w-none lg:grid-cols-2 lg:gap-y-16">
              <div v-for="feature in features" :key="feature.name" class="relative pl-16">
                <dt class="text-base font-semibold leading-7 text-gray-900">
                  <div class="absolute left-0 top-0 flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500/10 text-primary-500">
                    <component :is="feature.icon" class="h-6 w-6" aria-hidden="true" />
                  </div>
                  {{ __(feature.name) }}
                </dt>
                <dd class="mt-2 text-base leading-7 text-gray-600">{{ __(feature.description) }}</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      <hr class="divider"/>

      <cheap-package-preview-box :bg-from="'bg-white'" :bg-to="'bg-white'"/>

      <feature-preview-box/>

    </main>

  </guest-layout>
</template>

<script setup>
import GuestLayout from "@/Layouts/GuestLayout";
import VueWriter from 'vue-writer';

import {defineComponent, h} from "vue";
import FeaturePreviewBox from "@/Pages/Sites/FeatureSections/FeaturePreviewBox";
import CheapPackagePreviewBox from "@/Pages/Sites/FeatureSections/CheapPackagePreviewBox";
import PackagePricingBoxes from "@/Pages/Sites/FeatureSections/PackagePricingBoxes";

const features = [
  {
    name: 'Simplified Integration',
    description: 'Connect your applications or systems with LIVCK using our streamlined REST API. It provides a familiar and intuitive interface for accessing and manipulating LIVCK resources, simplifying the integration process.',
    icon: defineComponent({
      render() {
        return h('svg', {
          width: '24',
          height: '24',
          viewBox: '0 0 24 24',
          fill: 'none',
          xmlns: 'http://www.w3.org/2000/svg'
        }, [
          h('path', {
            d: 'M7.5 4.5C7.5 3.11929 8.61929 2 10 2C11.3807 2 12.5 3.11929 12.5 4.5V6H13.5C14.8978 6 15.5967 6 16.1481 6.22836C16.8831 6.53284 17.4672 7.11687 17.7716 7.85195C18 8.40326 18 9.10218 18 10.5H19.5C20.8807 10.5 22 11.6193 22 13C22 14.3807 20.8807 15.5 19.5 15.5H18V17.2C18 18.8802 18 19.7202 17.673 20.362C17.3854 20.9265 16.9265 21.3854 16.362 21.673C15.7202 22 14.8802 22 13.2 22H12.5V20.25C12.5 19.0074 11.4926 18 10.25 18C9.00736 18 8 19.0074 8 20.25V22H6.8C5.11984 22 4.27976 22 3.63803 21.673C3.07354 21.3854 2.6146 20.9265 2.32698 20.362C2 19.7202 2 18.8802 2 17.2V15.5H3.5C4.88071 15.5 6 14.3807 6 13C6 11.6193 4.88071 10.5 3.5 10.5H2C2 9.10218 2 8.40326 2.22836 7.85195C2.53284 7.11687 3.11687 6.53284 3.85195 6.22836C4.40326 6 5.10218 6 6.5 6H7.5V4.5Z',
            stroke: 'currentColor',
            'stroke-width': '2',
            'stroke-linecap': 'round',
            'stroke-linejoin': 'round'
          })
        ]);
      }
    }),
  },
  {
    name: 'Efficient Status Monitoring',
    description: 'Simplify status monitoring and enhance communication with the LIVCK API. Utilize the private API to create, update, and delete services, set up automated health checks, and manage incidents seamlessly.',
    icon: defineComponent({
      render() {
        return h('svg', {
          width: '24',
          height: '24',
          viewBox: '0 0 24 24',
          fill: 'none',
          xmlns: 'http://www.w3.org/2000/svg'
        }, [
          h('path', {
            d: 'M11 3.99998H6.8C5.11984 3.99998 4.27976 3.99998 3.63803 4.32696C3.07354 4.61458 2.6146 5.07353 2.32698 5.63801C2 6.27975 2 7.11983 2 8.79998V17.2C2 18.8801 2 19.7202 2.32698 20.362C2.6146 20.9264 3.07354 21.3854 3.63803 21.673C4.27976 22 5.11984 22 6.8 22H15.2C16.8802 22 17.7202 22 18.362 21.673C18.9265 21.3854 19.3854 20.9264 19.673 20.362C20 19.7202 20 18.8801 20 17.2V13M7.99997 16H9.67452C10.1637 16 10.4083 16 10.6385 15.9447C10.8425 15.8957 11.0376 15.8149 11.2166 15.7053C11.4184 15.5816 11.5914 15.4086 11.9373 15.0627L21.5 5.49998C22.3284 4.67156 22.3284 3.32841 21.5 2.49998C20.6716 1.67156 19.3284 1.67155 18.5 2.49998L8.93723 12.0627C8.59133 12.4086 8.41838 12.5816 8.29469 12.7834C8.18504 12.9624 8.10423 13.1574 8.05523 13.3615C7.99997 13.5917 7.99997 13.8363 7.99997 14.3255V16Z',
            stroke: 'currentColor',
            'stroke-width': '2',
            'stroke-linecap': 'round',
            'stroke-linejoin': 'round'
          })
        ]);
      }
    }),
  },
  {
    name: 'Guaranteed API Stability',
    description: 'Enjoy a stable and reliable API experience with LIVCK. We ensure consistency and integrity in our API responses, safeguarding against disruptions or failures in your development processes, even after updates or enhancements are implemented.',
    icon: defineComponent({
      render() {
        return h('svg', {
          width: '24',
          height: '24',
          viewBox: '0 0 24 24',
          fill: 'none',
          xmlns: 'http://www.w3.org/2000/svg'
        }, [
          h('path', {
            d: 'M14 17L17 14L14 11M10 7L7 10L10 13M7.8 21H16.2C17.8802 21 18.7202 21 19.362 20.673C19.9265 20.3854 20.3854 19.9265 20.673 19.362C21 18.7202 21 17.8802 21 16.2V7.8C21 6.11984 21 5.27976 20.673 4.63803C20.3854 4.07354 19.9265 3.6146 19.362 3.32698C18.7202 3 17.8802 3 16.2 3H7.8C6.11984 3 5.27976 3 4.63803 3.32698C4.07354 3.6146 3.6146 4.07354 3.32698 4.63803C3 5.27976 3 6.11984 3 7.8V16.2C3 17.8802 3 18.7202 3.32698 19.362C3.6146 19.9265 4.07354 20.3854 4.63803 20.673C5.27976 21 6.11984 21 7.8 21Z',
            stroke: 'currentColor',
            'stroke-width': '2',
            'stroke-linecap': 'round',
            'stroke-linejoin': 'round'
          })
        ]);
      }
    }),
  },
  {
    name: 'Comprehensive Status Management',
    description: 'Benefit from extensive API coverage for seamless status management. Our public and private APIs cater to different needs: the private API enables updates from third-party services, while the public API allows real-time status retrieval.',
    icon: defineComponent({
      render() {
        return h('svg', {
          width: '24',
          height: '24',
          viewBox: '0 0 24 24',
          fill: 'none',
          xmlns: 'http://www.w3.org/2000/svg'
        }, [
          h('path', {
            d: 'M20.5 7.27783L12 12.0001M12 12.0001L3.49997 7.27783M12 12.0001L12 21.5001M21 16.0586V7.94153C21 7.59889 21 7.42757 20.9495 7.27477C20.9049 7.13959 20.8318 7.01551 20.7354 6.91082C20.6263 6.79248 20.4766 6.70928 20.177 6.54288L12.777 2.43177C12.4934 2.27421 12.3516 2.19543 12.2015 2.16454C12.0685 2.13721 11.9315 2.13721 11.7986 2.16454C11.6484 2.19543 11.5066 2.27421 11.223 2.43177L3.82297 6.54288C3.52345 6.70928 3.37369 6.79248 3.26463 6.91082C3.16816 7.01551 3.09515 7.13959 3.05048 7.27477C3 7.42757 3 7.59889 3 7.94153V16.0586C3 16.4013 3 16.5726 3.05048 16.7254C3.09515 16.8606 3.16816 16.9847 3.26463 17.0893C3.37369 17.2077 3.52345 17.2909 3.82297 17.4573L11.223 21.5684C11.5066 21.726 11.6484 21.8047 11.7986 21.8356C11.9315 21.863 12.0685 21.863 12.2015 21.8356C12.3516 21.8047 12.4934 21.726 12.777 21.5684L20.177 17.4573C20.4766 17.2909 20.6263 17.2077 20.7354 17.0893C20.8318 16.9847 20.9049 16.8606 20.9495 16.7254C21 16.5726 21 16.4013 21 16.0586Z',
            stroke: 'currentColor',
            'stroke-width': '2',
            'stroke-linecap': 'round',
            'stroke-linejoin': 'round'
          }),
          h('path', {
            d: 'M16.5 9.5L7.5 4.5',
            stroke: 'currentColor',
            'stroke-width': '2',
            'stroke-linecap': 'round',
            'stroke-linejoin': 'round'
          })
        ]);
      }
    }),
  },
]
</script>